import type { Dish, Recipe } from '@/types/dish';
import { dessertFactories } from './mockDessertFactories';
import { createParmentierDish, dishFactories } from './mockMainCourseFactories';
import { createParmentierRecipe, recipeFactories } from './mockRecipeFactories';
import { starterFactories } from './mockStarterFactories';

// FONCTIONS PUBLIQUES

/**
 * Retourne tous les plats principaux mock avec traductions actuelles
 */
export function getMockMainCourse(): Dish[] {
  return Object.values(dishFactories).map((factory) => factory());
}

/**
 * Retourne toutes les entrées mock avec traductions actuelles
 */
export function getMockStarters(): Dish[] {
  return Object.values(starterFactories).map((factory) => factory());
}

/**
 * Retourne tous les desserts mock avec traductions actuelles
 */
export function getMockDesserts(): Dish[] {
  return Object.values(dessertFactories).map((factory) => factory());
}

/**
 * Trouve un plat mock par son ID
 */
export function findMockDishById(id: number): Dish | null {
  const factory = dishFactories[id] || starterFactories[id] || dessertFactories[id];
  return factory ? factory() : null;
}

/**
 * Fonction pour obtenir les titres traduits des plats mock
 */
export function getMockDishTitles(): string[] {
  return getMockMainCourse().map((d) => d.name);
}

/**
 * Map des titres vers les IDs des plats
 */
export function getMockDishIdByTitle(title: string): number | undefined {
  // Recherche dans tous les plats mock (main, starter, dessert)
  const allDishes = [...getMockMainCourse(), ...getMockStarters(), ...getMockDesserts()];
  const found = allDishes.find((d) => d.name === title);
  if (!found) {
    // Debug log pour voir le mapping
    console.warn('[Onboarding][DEBUG] Titre non trouvé dans les plats mock:', title);
    console.info(
      '[Onboarding][DEBUG] Titres disponibles:',
      allDishes.map((d) => d.name),
    );
  }
  return found?.id;
}

/**
 * Retourne toutes les recettes mock avec traductions actuelles
 */
export function getMockRecipes(): Recipe[] {
  return Object.values(recipeFactories).map((factory) => factory());
}

/**
 * Trouve une recette mock par l'ID du plat
 */
export function findMockRecipeByDishId(dishId: number): Recipe | null {
  const factory = recipeFactories[dishId];
  return factory ? factory() : null;
}

// EXPORTS STATIQUES POUR COMPATIBILITÉ

/**
 * Les plats principaux (IDs -1 à -5) - Export statique pour compatibilité
 */
export const mockMainCourse: Dish[] = getMockMainCourse();

/**
 * Les entrées - maintenant dynamiques
 */
export const mockStarters: Dish[] = getMockStarters();

/**
 * Les desserts - maintenant dynamiques
 */
export const mockDesserts: Dish[] = getMockDesserts();

// Export de compatibilité
export const mockDishes: Dish[] = [...mockMainCourse, ...mockStarters, ...mockDesserts];
export const mockDishTitles: string[] = mockDishes.map((d) => d.name);

// Recettes mock (maintenant dynamiques)
export const mockRecipes: Recipe[] = getMockRecipes();

/**
 * Fonction utilitaire pour obtenir les plats selon le type choisi
 */
export function getMockDishesByType(dishType: 'main' | 'starter' | 'dessert'): Dish[] {
  switch (dishType) {
    case 'main':
      return getMockMainCourse();
    case 'starter':
      return getMockStarters();
    case 'dessert':
      return getMockDesserts();
    default:
      return getMockMainCourse();
  }
}

/**
 * Fonction utilitaire pour obtenir les titres selon le type choisi
 */
export function getMockDishTitlesByType(dishType: 'main' | 'starter' | 'dessert'): string[] {
  return getMockDishesByType(dishType).map((d) => d.name);
}

/**
 * Fonction utilitaire pour obtenir une recette mock pour l'onboarding
 * Retourne le plat saumon avec les traductions à jour
 */
export function getMockDishForOnboarding(): Dish {
  return createParmentierDish();
}

/**
 * Fonction utilitaire pour obtenir la recette mock pour l'onboarding
 * Retourne la recette saumon avec les traductions à jour
 */
export function getMockRecipeForOnboarding(): Recipe {
  return createParmentierRecipe();
}
